import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { faMinus } from '@fortawesome/pro-light-svg-icons/faMinus';
import { faArrowsUpDown } from '@fortawesome/pro-light-svg-icons/faArrowsUpDown';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../../common/types';
import { usePlaylistChanges } from '../../hooks/usePlaylistChanges';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLImage } from '../SMLImage';
import { SMLLink } from '../SMLLink/SMLLink';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';
import styles from './PlaylistChangeTimeline.module.scss';

interface PlaylistChangeTimelineProps {
  playlistId: string;
  endDate?: Date;
}

interface ChangeEvent {
  trackId: string;
  trackName: string;
  artistName: string;
  imageUrl: string | null;
  date: string;
  type: 'songAdded' | 'songRemoved' | 'songMoved';
  position: number;
  previousPosition?: number;
}

interface GroupedEvents {
  date: string;
  events: ChangeEvent[];
}

const PAGE_SIZE = 30;

const eventIconMap = {
  songAdded: faPlus,
  songRemoved: faMinus,
  songMoved: faArrowsUpDown,
} as const;

const formatDateLabel = (dateString: string, intl: ReturnType<typeof useIntl>): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return intl.formatMessage({ id: 'app.sml.playlists.changes.today', defaultMessage: 'Today' });
  }
  if (diffDays === 1) {
    return intl.formatMessage({ id: 'app.sml.playlists.changes.yesterday', defaultMessage: 'Yesterday' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const getEventStyleClass = (type: string): string | undefined => {
  if (type === 'songAdded') {
    return styles.eventAdded;
  }
  if (type === 'songRemoved') {
    return styles.eventRemoved;
  }
  return styles.eventMoved;
};

const getIconStyleClass = (type: string): string | undefined => {
  if (type === 'songAdded') {
    return styles.iconAdded;
  }
  if (type === 'songRemoved') {
    return styles.iconRemoved;
  }
  return styles.iconMoved;
};

const getTextStyleClass = (type: string): string | undefined => {
  if (type === 'songAdded') {
    return styles.eventAddedText;
  }
  if (type === 'songRemoved') {
    return styles.eventRemovedText;
  }
  return styles.eventMovedText;
};

const getArtistStyleClass = (type: string): string | undefined => {
  if (type === 'songAdded') {
    return styles.eventAddedArtist;
  }
  if (type === 'songRemoved') {
    return styles.eventRemovedArtist;
  }
  return styles.eventMovedArtist;
};

const PlaylistChangeTimelineComponent: React.FC<PlaylistChangeTimelineProps> = ({ playlistId, endDate }) => {
  const intl = useIntl();
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PAGE_SIZE;

  const { data, isLoading, error, refetch } = usePlaylistChanges({
    playlistId,
    offset,
    limit: PAGE_SIZE,
    endDate,
  });

  const events = useMemo(() => data?.events ?? [], [data?.events]);

  const groupedEvents = useMemo<GroupedEvents[]>(() => {
    const groups = new Map<string, GroupedEvents['events']>();

    events.forEach((event) => {
      const existing = groups.get(event.date);
      if (existing !== undefined) {
        existing.push(event);
      } else {
        groups.set(event.date, [event]);
      }
    });

    return Array.from(groups.entries()).map(([date, evts]) => ({
      date,
      events: evts,
    }));
  }, [events]);

  const canGoBack = page > 1;
  const canGoNext = events.length === PAGE_SIZE;

  const handlePrevious = useCallback(() => {
    if (canGoBack) {
      setPage((p) => p - 1);
    }
  }, [canGoBack]);

  const handleNext = useCallback(() => {
    if (canGoNext) {
      setPage((p) => p + 1);
    }
  }, [canGoNext]);

  return (
    <Panel>
      <PanelHead title={intl.formatMessage({ id: 'app.sml.playlists.changes.title', defaultMessage: 'Changes' })} />
      <PanelBody>
        {isLoading && <LoadingView />}

        {error !== null && !isLoading && (
          <ErrorView
            error={error}
            retry={() => {
              refetch().catch(console.error);
            }}
          />
        )}

        {!isLoading && error === null && events.length === 0 && page === 1 && (
          <div className={styles.emptyState}>
            {intl.formatMessage({
              id: 'app.sml.playlists.changes.empty',
              defaultMessage: 'No changes recorded yet.',
            })}
          </div>
        )}

        {!isLoading && error === null && events.length > 0 && (
          <div className={styles.timeline}>
            {groupedEvents.map((group) => (
              <div key={group.date} className={styles.dateGroup}>
                <div className={styles.dateLabel}>{formatDateLabel(group.date, intl)}</div>
                {group.events.map((event) => (
                  <SMLLink
                    key={`${event.trackId}-${event.date}-${event.type}-${event.previousPosition ?? ''}-${event.position}`}
                    to={SMLRoutes.trackDetails}
                    params={{ trackId: event.trackId }}
                    className={`${styles.eventBubble} ${getEventStyleClass(event.type)}`}
                  >
                    <span className={styles.eventPosition}>
                      {event.type === 'songMoved' && event.previousPosition !== undefined
                        ? `#${event.previousPosition + 1} → #${event.position + 1}`
                        : `#${event.position + 1}`}
                    </span>
                    <FontAwesomeIcon
                      icon={eventIconMap[event.type]}
                      className={`${styles.eventIcon} ${getIconStyleClass(event.type)}`}
                    />
                    <SMLImage
                      src={event.imageUrl}
                      alt={event.trackName}
                      width={28}
                      makeSquareFit
                      className={styles.eventImage}
                    />
                    <div className={styles.eventInfo}>
                      <div className={`${styles.eventTrackName} ${getTextStyleClass(event.type)}`}>
                        {event.trackName}
                      </div>
                      <div className={`${styles.eventArtist} ${getArtistStyleClass(event.type)}`}>
                        {event.artistName}
                      </div>
                    </div>
                  </SMLLink>
                ))}
              </div>
            ))}
          </div>
        )}

        {!isLoading && error === null && (canGoBack || canGoNext) && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={`${styles.paginationButton} ${!canGoBack ? styles.disabled : ''}`}
              onClick={handlePrevious}
              disabled={!canGoBack}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className={styles.pageInfo}>
              {intl.formatMessage({ id: 'app.sml.playlists.changes.page', defaultMessage: 'Page {page}' }, { page })}
            </span>
            <button
              type="button"
              className={`${styles.paginationButton} ${!canGoNext ? styles.disabled : ''}`}
              onClick={handleNext}
              disabled={!canGoNext}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const PlaylistChangeTimeline = TypedMemo(PlaylistChangeTimelineComponent);
